import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { Schema } from "effect";
import { useState } from "react";
import { toast } from "sonner";

import { useUserInfo } from "@/hooks/users/use-user-info";
import { useTRPC } from "@/infrastructure/trpc/react";
import { useAppForm } from "@/shared/forms/form-context";
import { FormInput } from "@/shared/forms/form-input";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
  OrganizationRoles,
  EntityName,
} from "@vivus/common";

const OrgSchema = Schema.Struct({
  name: EntityName.pipe(
    Schema.annotate({
      message: "Organization name is required",
    })
  ),
});

export const Route = createFileRoute(
  "/app/organization/$id/settings/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showDeleteDialog, setShowDeleteDialog] =
    useState(false);
  const [confirmationName, setConfirmationName] =
    useState("");

  const { data } = useSuspenseQuery(
    trpc.organization.getById.queryOptions({ id })
  );
  const organization = data?.organization;

  const { data: members } = useSuspenseQuery(
    trpc.organizationMember.getMembersByOrganizationId.queryOptions(
      { organizationId: id }
    )
  );
  const { data: userInfo } = useUserInfo();
  const currentMember = members?.find(
    (m) => m.id === userInfo?.id
  );
  const canManage =
    currentMember?.role === OrganizationRoles.Owner ||
    currentMember?.role === OrganizationRoles.Admin;

  const setOrganizationName = useMutation(
    trpc.organization.setOrganizationName.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization name saved!");
        await queryClient.invalidateQueries(
          trpc.organization.getOrganizationsDetails.queryOptions()
        );
        await queryClient.invalidateQueries(
          trpc.organization.getById.queryOptions({ id })
        );
      },
      onError: (error) => {
        toast.error(
          error.message ||
            "Failed to save organization name."
        );
      },
    })
  );

  const deleteOrganization = useMutation(
    trpc.organization.deleteOrganization.mutationOptions({
      onSuccess: async () => {
        toast.success("Organization deleted successfully");
        await queryClient.invalidateQueries(
          trpc.organization.getUserOrganizations.queryOptions()
        );
        await queryClient.invalidateQueries(
          trpc.organization.getOrganizationsDetails.queryOptions()
        );
        void navigate({ to: "/app" });
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to delete organization"
        );
      },
    })
  );

  const form = useAppForm({
    defaultValues: { name: organization?.name ?? "" },
    validators: {
      onChange: Schema.toStandardSchemaV1(OrgSchema),
    },
    onSubmit: async ({ value }) => {
      await setOrganizationName.mutateAsync({
        id,
        name: value.name,
      });
    },
  });

  return (
    <>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!canManage) return;
          await form.handleSubmit();
        }}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-row items-end gap-2">
          <div className="flex flex-1 flex-col">
            <FormInput
              form={form}
              name="name"
              label="Organization Name"
              placeholder="Enter organization name"
              required
              disabled={!canManage}
            />
          </div>
          <Button
            type="submit"
            disabled={
              !canManage ||
              form.state.isSubmitting ||
              setOrganizationName.isPending
            }
            className="h-9"
          >
            {form.state.isSubmitting ||
            setOrganizationName.isPending
              ? "Saving..."
              : "Save"}
          </Button>
        </div>
      </form>

      {/* Danger Zone */}
      <div className="mt-8 border-t pt-6">
        <Card variant="danger">
          <CardContent variant="p6">
            <h3 className="mb-2 text-lg font-semibold text-destructive">
              Danger Zone
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Permanently delete this organization and all
              of its projects, API keys, and data. This
              action cannot be undone.
            </p>
            <Button
              variant="destructive"
              onClick={() => {
                setShowDeleteDialog(true);
              }}
              disabled={
                !canManage || deleteOrganization.isPending
              }
            >
              Delete Organization
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeleteDialog(false);
            setConfirmationName("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Organization</DialogTitle>
            <DialogDescription>
              This action will permanently delete your
              organization, including all projects, API
              keys, integrations, and associated data. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-2 text-sm font-medium">
                To confirm, type "{organization?.name}"
                below:
              </p>
              <Input
                variant="mono"
                type="text"
                value={confirmationName}
                onChange={(e) => {
                  setConfirmationName(e.target.value);
                }}
                placeholder={organization?.name}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setConfirmationName("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  deleteOrganization.mutate({
                    organizationId: id,
                    confirmationName,
                  });
                }}
                disabled={
                  !canManage ||
                  confirmationName !== organization?.name ||
                  deleteOrganization.isPending
                }
              >
                {deleteOrganization.isPending
                  ? "Deleting..."
                  : "Delete Organization"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
